import { ZRoutingGraph, } from "../schemas/routingGraph.schema";
const debug = true;
function dot(a, b) { return a.x * b.x + a.y * b.y + a.z * b.z; }
function add(a, b) { return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z }; }
function scale(v, s) { return { x: v.x * s, y: v.y * s, z: v.z * s }; }
function subtract(a, b) { return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }; }
function distance(a, b) { return len(subtract(a, b)); }
function len(a) { return Math.sqrt(dot(a, a)); }
function normalize(v) {
    const l = len(v);
    return l > 1e-12 ? scale(v, 1 / l) : undefined;
}
export function buildRoutingGraphFromMcBimPipeGeometries(args) {
    const { projectNo, systemCode, floorName, systemName, roomNumber, mcBimPipeGeometries, mcBimConnectionGeometries, } = args;
    void mcBimConnectionGeometries;
    const segments = extractSegmentsFromMcBimGeometries(mcBimPipeGeometries);
    const connectors = extractConnectorsFromMcBimGeometries(mcBimConnectionGeometries);
    const extendedSegments = extendSegmentsToConnect(segments, connectors);
    const { nodes, nodeIdByKey } = buildNodesFromSegments(extendedSegments);
    const edges = buildEdgesFromSegments(extendedSegments, nodeIdByKey);
    return ZRoutingGraph.parse({
        projectNo,
        systemCode,
        systemName,
        floorName,
        roomNumber,
        nodes,
        edges,
    });
}
function extractSegmentsFromMcBimGeometries(pipesWithGeometry) {
    const segments = [];
    const connectors = [];
    for (let geom of pipesWithGeometry) {
        const obbList = geom.geometry.Obb3DWorldMmList;
        if (obbList?.length == 1) {
            // Sort edges,
            const sides = [{ dist: obbList[0].extentAM, dir: obbList[0].axisA }, { dist: obbList[0].extentBM, dir: obbList[0].axisB }, { dist: obbList[0].extentCM, dir: obbList[0].axisC }];
            const sortedSides = sides.sort((a, b) => a.dist - b.dist);
            if (sortedSides[1].dist < sortedSides[0].dist * 1.001) {
                const endP0 = add(obbList[0].centerM, scale(sortedSides[2].dir, sortedSides[2].dist));
                const endP1 = add(obbList[0].centerM, scale(sortedSides[2].dir, -sortedSides[2].dist));
                segments.push({ from: endP0, to: endP1 });
            }
            else if (sortedSides[2].dist < sortedSides[1].dist * 1.001) {
                const endP0 = add(obbList[0].centerM, scale(sortedSides[0].dir, sortedSides[0].dist));
                const endP1 = add(obbList[0].centerM, scale(sortedSides[0].dir, -sortedSides[0].dist));
                segments.push({ from: endP0, to: endP1 });
            }
            else {
                connectors.push({
                    positionM: obbList[0].centerM,
                    radiusM: sortedSides[2].dist
                });
            }
        }
        else {
            console.log("MULTI GEOM pipe found:", obbList);
        }
    }
    return segments;
}
function extractConnectorsFromMcBimGeometries(pipesWithGeometry) {
    const segments = [];
    const connectors = [];
    let id = 0;
    for (let geom of pipesWithGeometry) {
        const obbList = geom.geometry.Obb3DWorldMmList;
        if (obbList?.length == 1) {
            // Sort edges,
            const sides = [{ dist: obbList[0].extentAM, dir: obbList[0].axisA }, { dist: obbList[0].extentBM, dir: obbList[0].axisB }, { dist: obbList[0].extentCM, dir: obbList[0].axisC }];
            const sortedSides = sides.sort((a, b) => a.dist - b.dist);
            connectors.push({
                positionM: obbList[0].centerM,
                radiusM: sortedSides[2].dist
            });
        }
        else {
            console.log("MULTI GEOM pipe found:", obbList);
        }
    }
    return connectors;
}
function extendSegmentsToConnect(segments, connectors) {
    const result = segments.map(s => ({ ...s }));
    for (let i = 0; i < result.length; i++) {
        for (let j = i + 1; j < result.length; j++) {
            const meet = pipesWouldMeet(result[i], result[j], 0.05);
            if (!meet)
                continue;
            const tolerance = 0;
            const extensionNeeded = meet.ext1 ** 2 + meet.ext2 ** 2;
            const bridges = connectors.some(c => connectorBridgesPoint(meet.meetPoint, c, tolerance) && 4 * c.radiusM ** 2 > extensionNeeded);
            if (!bridges)
                continue;
            // Snap the closer endpoint of each segment to the shared meet point
            if (distance(result[i].from, meet.meetPoint) < distance(result[i].to, meet.meetPoint)) {
                result[i] = { ...result[i], from: meet.meetPoint };
            }
            else {
                result[i] = { ...result[i], to: meet.meetPoint };
            }
            if (distance(result[j].from, meet.meetPoint) < distance(result[j].to, meet.meetPoint)) {
                result[j] = { ...result[j], from: meet.meetPoint };
            }
            else {
                result[j] = { ...result[j], to: meet.meetPoint };
            }
        }
    }
    return result;
}
function buildNodesFromSegments(segments) {
    const nodes = [];
    const nodeIdByKey = new Map();
    for (const segment of segments) {
        ensureNode(segment.from, nodes, nodeIdByKey);
        ensureNode(segment.to, nodes, nodeIdByKey);
    }
    return { nodes, nodeIdByKey };
}
function buildEdgesFromSegments(segments, nodeIdByKey) {
    return segments.map((segment, index) => ({
        id: `e${index + 1}`,
        from: nodeIdByKey.get(pointKey(segment.from)) ?? "",
        to: nodeIdByKey.get(pointKey(segment.to)) ?? "",
        compCode: segment.compCode ?? `PIPE-${index + 1}`,
        profile: "CIRCULAR",
        size: { diameterM: 0.1 },
    }));
}
function ensureNode(point, nodes, nodeIdByKey) {
    const key = pointKey(point);
    if (nodeIdByKey.has(key)) {
        return;
    }
    const id = `n${nodes.length + 1}`;
    nodeIdByKey.set(key, id);
    nodes.push({ id, posM: [point.x, point.y, point.z], kind: "JUNCTION" });
}
function pointKey(point) {
    return `${point.x}|${point.y}|${point.z}`;
}
function segmentToLine(seg) {
    const p = seg.from;
    const dir = normalize(add(seg.to, scale(seg.from, -1)));
    return { p, dir };
}
function closestPointsBetweenLines(p1, d1, p2, d2) {
    const r = { x: p1.x - p2.x, y: p1.y - p2.y, z: p1.z - p2.z };
    const a = dot(d1, d1);
    const e = dot(d2, d2);
    const f = dot(d2, r);
    const b = dot(d1, d2);
    const c = dot(d1, r);
    const denom = a * e - b * b;
    if (Math.abs(denom) < 1e-9) {
        return undefined; // parallel
    }
    const s = (b * f - c * e) / denom;
    const t = (a * f - b * c) / denom;
    const cp1 = add(p1, scale(d1, s));
    const cp2 = add(p2, scale(d2, t));
    return { cp1, cp2 };
}
function pipesWouldMeet(s1, s2, tolerance = 0.05 // meters
) {
    const l1 = segmentToLine(s1);
    const l2 = segmentToLine(s2);
    if (l1.dir == undefined || l2.dir == undefined)
        return undefined;
    const res = closestPointsBetweenLines(l1.p, l1.dir, l2.p, l2.dir);
    if (!res) {
        // Parallel lines: find the closest endpoint pair and treat that gap as the connection
        return pipesWouldMeetParallel(s1, s2);
    }
    const dist = distance(res.cp1, res.cp2);
    if (dist >= tolerance)
        return undefined;
    const meetPoint = {
        x: (res.cp1.x + res.cp2.x) / 2,
        y: (res.cp1.y + res.cp2.y) / 2,
        z: (res.cp1.z + res.cp2.z) / 2,
    };
    // s/t: signed distance from segment.from along the (normalized) line direction to cp1/cp2
    const s = dot(subtract(res.cp1, l1.p), l1.dir);
    const t = dot(subtract(res.cp2, l2.p), l2.dir);
    const seg1Len = distance(s1.from, s1.to);
    const seg2Len = distance(s2.from, s2.to);
    // Extension: how far beyond the segment endpoints the meet point lies
    const ext1 = s < 0 ? -s : s > seg1Len ? s - seg1Len : 0;
    const ext2 = t < 0 ? -t : t > seg2Len ? t - seg2Len : 0;
    return { meetPoint, ext1, ext2 };
}
function pipesWouldMeetParallel(s1, s2) {
    // Find the closest pair of endpoints across the two segments
    const pairs = [
        [s1.from, s2.from],
        [s1.from, s2.to],
        [s1.to, s2.from],
        [s1.to, s2.to],
    ];
    let bestDist = Infinity;
    let bestA = s1.from;
    let bestB = s2.from;
    for (const [a, b] of pairs) {
        const d = distance(a, b);
        if (d < bestDist) {
            bestDist = d;
            bestA = a;
            bestB = b;
        }
    }
    const meetPoint = {
        x: (bestA.x + bestB.x) / 2,
        y: (bestA.y + bestB.y) / 2,
        z: (bestA.z + bestB.z) / 2,
    };
    return { meetPoint, ext1: bestDist / 2, ext2: bestDist / 2 };
}
function connectorBridgesPoint(point, connector, tolerance = 0.05) {
    const d = distance(point, connector.positionM);
    return d < connector.radiusM + tolerance;
}
/*function connectorBridgesSegments(
  s1: RoutingSegmentCandidate,
  s2: RoutingSegmentCandidate,
  connectors: ConnectionPoint[]
){

  const meet = pipesWouldMeet(s1, s2);
  if (!meet) return false;

  const extensionNeeded = Math.max(meet.ext1, meet.ext2);

  for (const c of connectors){
    if (connectorBridgesPoint(meet.meetPoint, c) && c.radiusM >= extensionNeeded){
      return true;
    }
  }

  return false;
}*/
/*function segmentsCanConnect(
  s1: RoutingSegmentCandidate,
  s2: RoutingSegmentCandidate,
  connectors: ConnectionPoint[]
){
  return connectorBridgesSegments(s1, s2, connectors);
}*/ 
